import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Filter as FilterIcon, Search, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { ACTION_BTN } from 'shared/styles/topBarActions';
import { getAlertFieldValues } from 'features/alerts/api/alertsApi';

/**
 * The filter rows on an alert.
 *
 * Alerts' own, sharing nothing with the filter builders elsewhere in the app.
 * That is deliberate: this reads one row at a time out of a schema alerts
 * defines, and a change here reaches nothing else.
 *
 * Filter shape, matching services/alerts/filter.js:
 *   { groups: [ { op: 'AND', rules: [{ field, operator, value }] } ] }
 *
 * One AND group. Rules read top to bottom as one sentence: WHERE this, AND
 * this. An OR builder is a different control and is not what people reach for
 * when they say "tell me when checkout starts failing".
 */

/** A filter row is three form controls in a line, styled as the Settings inputs are. */
const ROW = 'flex flex-wrap items-center gap-1.5';
const CONNECTOR = 'w-[46px] shrink-0 text-[13px] text-[color:var(--text-muted)]';
const CONTROL = 'h-8 rounded-md border border-[color:var(--border-default)] bg-transparent text-[13px] text-[color:var(--text-primary)] outline-none transition-colors';
const FIELD_BTN = `${CONTROL} inline-flex max-w-[220px] items-center gap-1.5 px-2.5 hover:border-[color:var(--border-strong)]`;
const TEXT_INPUT = `${CONTROL} w-48 px-2.5 placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)]`;

const newId = () => `r${Math.random().toString(36).slice(2, 9)}`;
const rulesOf = (value) => (value?.groups?.[0]?.rules) || [];
const withRules = (rules) => ({ groups: rules.length ? [{ op: 'AND', rules }] : [] });

/**
 * A value the field has held, or anything else you type.
 *
 * Suggestions are a convenience, never a constraint: an error message you want
 * to alert on may not have happened yet, and the string you saw last week
 * gains an id next release. So the box is always typable and what you type is
 * always accepted.
 */
function ValueInput({ scope, field, type, value, onChange, evalLineageId = null }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value == null ? '' : String(value));
  const [suggestions, setSuggestions] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => { setText(value == null ? '' : String(value)); }, [value]);

  // Suggestions are fetched when the box is first focused, not when the row
  // appears: a rule sitting on the form costs nothing. Typing asks the server
  // for matches (after a short pause) rather than filtering a pre-fetched list.
  // An eval's endings are the exception: that control is a named pick-list, so
  // it needs its few values up front to know to render as one.
  const [asked, setAsked] = useState(scope === 'eval');
  const needle = text.trim();
  useEffect(() => {
    if (type !== 'string' || !field || !asked) { setSuggestions([]); return undefined; }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      getAlertFieldValues(scope, field, evalLineageId, { q: scope === 'eval' ? '' : needle, limit: 20, signal: ac.signal })
        .then((v) => { if (!ac.signal.aborted) setSuggestions(v); })
        .catch(() => { if (!ac.signal.aborted) setSuggestions([]); });
    }, needle && scope !== 'eval' ? 250 : 0);
    return () => { clearTimeout(timer); ac.abort(); };
  }, [scope, field, type, evalLineageId, asked, needle]);

  const shown = useMemo(() => suggestions.slice(0, 8), [suggestions]);

  const commit = (v) => {
    setText(v);
    onChange(type === 'number' && v !== '' && Number.isFinite(Number(v)) ? Number(v) : v);
  };

  // Values with names of their own — an eval's endings, stored by step id,
  // read by name — are picked from a list rather than typed as ids.
  if (suggestions.length && suggestions.every((sg) => sg.label)) {
    const current = Array.isArray(value) ? value[0] : value;
    return (
      <span className="inline-block w-56">
        <BrandDropdown compact ariaLabel={`${field} value`} value={current == null ? '' : String(current)}
          onChange={(v) => onChange(v)} placeholder="Pick one"
          options={suggestions.map((sg) => ({ value: sg.value, label: `${sg.label}${sg.count ? ` · ${sg.count}` : ''}` }))} />
      </span>
    );
  }

  return (
    <span className="relative" ref={boxRef}>
      <input
        className={TEXT_INPUT}
        value={text}
        inputMode={type === 'number' ? 'decimal' : undefined}
        placeholder={type === 'number' ? 'number' : 'value'}
        onChange={(e) => commit(e.target.value)}
        onFocus={() => { setAsked(true); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && shown.length > 0 && (
        <span className="absolute left-0 top-full z-[70] mt-1 block max-h-[200px] w-56 overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] py-1 shadow-token-lg">
          {shown.map((s) => (
            <button
              key={s.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(s.value); setOpen(false); }}
              className="flex w-full items-center justify-between gap-2 px-2.5 py-1 text-left text-[12px] text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            >
              <span className="truncate">{s.value}</span>
              <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--text-disabled)]">{s.count}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/** Pick a field. Grouped, searchable, anchored under the control that opened it. */
function FieldPicker({ fields, anchorRef, onPick, onClose }) {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const rect = anchorRef.current?.getBoundingClientRect();

  useEffect(() => { const t = setTimeout(() => inputRef.current?.focus(), 30); return () => clearTimeout(t); }, []);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = new Map();
    for (const f of fields) {
      if (needle && !f.path.toLowerCase().includes(needle) && !String(f.label || '').toLowerCase().includes(needle)) continue;
      if (!out.has(f.group)) out.set(f.group, []);
      out.get(f.group).push(f);
    }
    return [...out.entries()];
  }, [fields, q]);

  if (!rect) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 z-[90]" onClick={onClose} />
      <div
        className="fixed z-[91] flex max-h-[320px] w-[320px] flex-col overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg"
        style={{ top: Math.min(rect.bottom + 4, window.innerHeight - 340), left: Math.min(rect.left, window.innerWidth - 336) }}
      >
        <div className="flex items-center gap-2 border-b border-[color:var(--border-default)] px-2.5 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search fields…"
            className="flex-1 bg-transparent text-[12.5px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {groups.length === 0 && (
            <p className="px-3 py-4 text-center text-[12px] text-[color:var(--text-muted)]">Nothing matches</p>
          )}
          {groups.map(([group, items]) => (
            <div key={group}>
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{group}</p>
              {items.map((f) => (
                <button
                  key={f.path}
                  type="button"
                  onClick={() => onPick(f)}
                  className="flex w-full items-baseline justify-between gap-2 px-3 py-1.5 text-left hover:bg-surface-hover"
                >
                  <span className="truncate text-[12px] text-[color:var(--text-primary)]">{f.label || f.path}</span>
                  <span className="shrink-0 text-[10px] text-[color:var(--text-muted)]">{f.type}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function AlertFilters({ value, onChange, schema, scope, evalLineageId = null }) {
  const rules = rulesOf(value);
  const [picking, setPicking] = useState(null);
  const addRef = useRef(null);
  const fieldRefs = useRef([]);

  const fields = schema?.fields || [];
  const operators = schema?.operators || {};
  const byType = schema?.operators_by_type || { string: [], number: [] };
  const fieldByPath = useMemo(() => new Map(fields.map((f) => [f.path, f])), [fields]);
  const typeOf = useCallback((path) => fieldByPath.get(path)?.type || 'string', [fieldByPath]);

  const setRules = (next) => onChange(withRules(next));
  const patch = (i, updates) => setRules(rules.map((r, idx) => (idx === i ? { ...r, ...updates } : r)));
  const remove = (i) => setRules(rules.filter((_, idx) => idx !== i));

  /**
   * Picking a field, whether adding a row or changing one. The operator resets
   * when the type changes, because "contains" on a number is not a question.
   */
  const pickField = (field) => {
    const ops = byType[field.type] || [];
    if (picking?.index != null) {
      const current = rules[picking.index];
      const sameType = typeOf(current.field) === field.type;
      // A fixed list of choices (Error category) never holds the old field's value.
      const choices = field.choices || fieldByPath.get(current.field)?.choices;
      patch(picking.index, {
        field: field.path,
        ...(sameType ? {} : { operator: ops[0] || 'is', value: '' }),
        ...(sameType && choices && current.field !== field.path ? { value: '' } : {}),
      });
    } else {
      setRules([...rules, { _id: newId(), field: field.path, operator: ops[0] || 'is', value: '' }]);
    }
    setPicking(null);
  };

  return (
    <div className="space-y-1.5">
      {rules.map((r, i) => {
        const type = typeOf(r.field);
        const def = fieldByPath.get(r.field);
        const opOptions = (byType[type] || []).map((k) => ({ value: k, label: operators[k]?.label || k }));
        const needsValue = operators[r.operator]?.needsValue !== false;
        return (
          <div key={r._id || `${r.field}-${i}`} className={ROW}>
            <span className={CONNECTOR}>{i === 0 ? 'Where' : 'And'}</span>
            {/* The field is a control too: changing your mind about which
                field should not mean deleting the row and starting again. */}
            <button
              type="button"
              ref={(el) => { fieldRefs.current[i] = el; }}
              onClick={() => setPicking({ index: i })}
              title={r.field}
              className={FIELD_BTN}
            >
              <span className="truncate">{def?.label || r.field}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            </button>
            <div style={{ minWidth: 118, width: 'max-content' }}>
              <BrandDropdown
                value={r.operator}
                onChange={(op) => patch(i, {
                  operator: op,
                  ...(operators[op]?.needsValue === false ? { value: null } : {}),
                })}
                options={opOptions}
                menuMinWidth={190}
              />
            </div>
            {needsValue && (
              <ValueInput
                scope={scope}
                evalLineageId={evalLineageId}
                field={r.field}
                type={type}
                value={r.value}
                onChange={(v) => patch(i, { value: v })}
              />
            )}
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={`Remove the filter on ${def?.label || r.field}`}
              className="rounded p-1.5 text-[color:var(--text-muted)] transition-colors hover:bg-danger-soft hover:text-danger"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}

      <button
        ref={addRef}
        type="button"
        onClick={() => setPicking({ adding: true })}
        className={ACTION_BTN}
      >
        <FilterIcon className="h-3.5 w-3.5" aria-hidden />
        Filter
      </button>

      {picking && (
        <FieldPicker
          fields={fields}
          anchorRef={picking.index != null ? { current: fieldRefs.current[picking.index] } : addRef}
          onPick={pickField}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}
